import { useState, useEffect } from 'react';
import { api } from '../../api/client';

interface AiProxyConfig {
  proxy_url: string;
  virtual_key_masked: string;
  has_virtual_key: boolean;
  default_chat_model: string;
  default_fast_model: string;
  default_vision_model: string;
  updated_at?: string;
}

const DEFAULT_CONFIG: AiProxyConfig = {
  proxy_url: 'https://ai-proxy.wowerix.com/v1',
  virtual_key_masked: '',
  has_virtual_key: false,
  default_chat_model: 'smart-default',
  default_fast_model: 'fast-default',
  default_vision_model: 'gemini-2.5-flash-lite',
};

export function AiProxySettings() {
  const [config, setConfig] = useState<AiProxyConfig>(DEFAULT_CONFIG);
  const [virtualKeyInput, setVirtualKeyInput] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Test state
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    status?: number;
    latency_ms?: number;
    routed_tier?: string;
    edge_colo?: string;
    request_id?: string;
    reply?: string;
    error?: string;
  } | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const data = await api.getAiProxyConfig();
      setConfig({ ...DEFAULT_CONFIG, ...data });
    } catch (e) {
      console.error('Failed to load AI proxy config', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.saveAiProxyConfig({
        proxy_url: config.proxy_url,
        virtual_key: virtualKeyInput.trim() || undefined,
        default_chat_model: config.default_chat_model,
        default_fast_model: config.default_fast_model,
        default_vision_model: config.default_vision_model
      });
      setSaved(true);
      setVirtualKeyInput('');
      await loadConfig();
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      alert('Lưu cấu hình thất bại: ' + (e.message || 'Lỗi không xác định'));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.testAiProxyConnection({
        proxy_url: config.proxy_url,
        virtual_key: virtualKeyInput.trim() || undefined
      });
      setTestResult(res);
    } catch (e: any) {
      setTestResult({
        ok: false,
        error: e.message || 'Không thể kết nối đến AI Proxy'
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px', flexDirection: 'column' }}>
        <div className="spinner" style={{ width: 32, height: 32 }} />
        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Đang nạp cấu hình Erix AI Proxy...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: '28px 32px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
              🔀 Cổng Erix AI Proxy Gateway
            </h1>
            <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '12px', background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', border: '1px solid rgba(56, 189, 248, 0.3)' }}>
              Spec v2.0
            </span>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '6px', marginBottom: 0 }}>
            Quản lý cổng định tuyến AI thống nhất toàn hệ sinh thái Erix. Tự động chuyển tầng (Tier 0 ➔ Tier 3), chống nghẽn và tối ưu chi phí qua Cloudflare AI Gateway.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            className="btn btn--secondary"
            onClick={handleTest}
            disabled={testing}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {testing ? (
              <>
                <div className="spinner" style={{ width: 14, height: 14 }} />
                <span>Đang gửi Probe...</span>
              </>
            ) : (
              <>
                <span>⚡ Kiểm Tra Kết Nối</span>
              </>
            )}
          </button>

          <button
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {saving ? (
              <>
                <div className="spinner" style={{ width: 14, height: 14 }} />
                <span>Đang lưu...</span>
              </>
            ) : saved ? (
              <>
                <span>✓ Đã Lưu</span>
              </>
            ) : (
              <>
                <span>💾 Lưu Cấu Hình</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Architecture Alert Banner */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.08) 0%, rgba(124, 58, 237, 0.08) 100%)',
        border: '1px solid rgba(6, 182, 212, 0.25)',
        borderRadius: '16px',
        padding: '20px 24px',
        marginBottom: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px' }}>🌐</span>
          <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>
            Nguyên Tắc Kiến Trúc: Một Cổng Duy Nhất & Chuyển Tầng Trong Suốt (Zero SPOF)
          </span>
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          ErixOne không gọi trực tiếp API của từng nhà cung cấp (Google, DeepSeek, Cloudflare). Mọi luồng suy luận (RAG Synthesis, Document Vision, Cognitive Coaching) đều đi qua <strong><code>https://ai-proxy.wowerix.com/v1</code></strong> và được phân tầng tự động:
          <div style={{ display: 'flex', gap: '16px', marginTop: '8px', flexWrap: 'wrap' }}>
            <span style={{ padding: '4px 10px', background: 'rgba(56, 189, 248, 0.12)', borderRadius: '6px', color: '#38bdf8' }}>
              ⚡ Tầng 0: Workers AI + Edge Cache ($0)
            </span>
            <span style={{ padding: '4px 10px', background: 'rgba(168, 85, 247, 0.12)', borderRadius: '6px', color: '#c084fc' }}>
              🚀 Tầng 1: Fly.io US-East (Gemini Key Pool)
            </span>
            <span style={{ padding: '4px 10px', background: 'rgba(245, 158, 11, 0.12)', borderRadius: '6px', color: '#fbbf24' }}>
              🛡️ Tầng 2: OVH VPS Singapore Failover
            </span>
            <span style={{ padding: '4px 10px', background: 'rgba(239, 68, 68, 0.12)', borderRadius: '6px', color: '#f87171' }}>
              🚨 Tầng 3: DeepSeek V3 Direct Fallback
            </span>
          </div>
        </div>
      </div>

      {/* Main Form Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', marginBottom: '28px' }}>
        {/* Card 1: Endpoints & Keys */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid var(--border)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '16px' }}>
            🔑 Điểm Cuối & Khóa Định Danh (Virtual Key)
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                AI Proxy Ingress URL
              </label>
              <input
                type="text"
                className="input"
                value={config.proxy_url}
                onChange={e => setConfig({ ...config, proxy_url: e.target.value })}
                style={{ width: '100%', fontFamily: 'monospace', fontSize: '13px' }}
                placeholder="https://ai-proxy.wowerix.com/v1"
              />
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginTop: '4px' }}>
                Chuẩn OpenAPI base URL (tương thích OpenAI SDK)
              </span>
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                  Erix Virtual Key (Noisy Neighbor Isolation)
                </label>
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  style={{ background: 'none', border: 'none', color: '#38bdf8', fontSize: '11px', cursor: 'pointer' }}
                >
                  {showKey ? 'Ẩn khóa' : 'Hiện khóa'}
                </button>
              </div>

              <input
                type={showKey ? 'text' : 'password'}
                className="input"
                value={virtualKeyInput}
                onChange={e => setVirtualKeyInput(e.target.value)}
                placeholder={config.virtual_key_masked || 'erix_live_one_33ced8e6...'}
                style={{ width: '100%', fontFamily: 'monospace', fontSize: '13px' }}
              />

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
                <span style={{ fontSize: '11px', color: config.has_virtual_key ? '#34d399' : '#fbbf24' }}>
                  {config.has_virtual_key ? `✓ Đang áp dụng: ${config.virtual_key_masked}` : '⚠️ Chưa cấu hình key riêng (Dùng fallback)'}
                </span>
                <a
                  href="https://care.wowerix.com"
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: '11px', color: '#38bdf8', textDecoration: 'none' }}
                >
                  Lấy Key trên ErixCare ➔
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Model Mapping */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid var(--border)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '16px' }}>
            🤖 Định Tuyến Model Mặc Định (Model Catalog)
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                Model Chat & RAG Synthesis
              </label>
              <select
                className="input"
                value={config.default_chat_model}
                onChange={e => setConfig({ ...config, default_chat_model: e.target.value })}
                style={{ width: '100%', fontSize: '13px' }}
              >
                <option value="smart-default">smart-default (Tự động chọn Gemini 2.5 Flash / Claude tối ưu)</option>
                <option value="gemini-2.5-flash">gemini-2.5-flash (Google Gemini 2.5 Đa phương thức)</option>
                <option value="gemini-2.5-pro">gemini-2.5-pro (Ngữ cảnh siêu dài 2 triệu tokens)</option>
                <option value="deepseek-chat">deepseek-chat (DeepSeek V3 Chuyên lập trình & văn phong)</option>
                <option value="deepseek-r1-distill">deepseek-r1-distill (Suy luận toán học, logic sâu)</option>
                <option value="llama-3.1-8b">llama-3.1-8b (Cloudflare Workers AI &lt;50ms)</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                Model Tác Vụ Nhanh (Fast / Reflex)
              </label>
              <select
                className="input"
                value={config.default_fast_model}
                onChange={e => setConfig({ ...config, default_fast_model: e.target.value })}
                style={{ width: '100%', fontSize: '13px' }}
              >
                <option value="fast-default">fast-default (Tự động trỏ sang Gemini 2.5 Flash Lite)</option>
                <option value="gemini-2.5-flash-lite">gemini-2.5-flash-lite</option>
                <option value="llama-3.2-3b">llama-3.2-3b (Tác vụ siêu nhẹ &lt;30ms)</option>
                <option value="llama-3.1-8b">llama-3.1-8b</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                Model Trích Xuất Tài Liệu & Vision (OCR)
              </label>
              <select
                className="input"
                value={config.default_vision_model}
                onChange={e => setConfig({ ...config, default_vision_model: e.target.value })}
                style={{ width: '100%', fontSize: '13px' }}
              >
                <option value="gemini-2.5-flash-lite">gemini-2.5-flash-lite (Tiết kiệm, tốc độ cao)</option>
                <option value="gemini-2.5-flash">gemini-2.5-flash (Nhận diện sơ đồ, biểu bảng phức tạp)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Test Console & Observability Card */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid var(--border)',
        borderRadius: '16px',
        padding: '24px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>
              📡 Bảng Giám Sát Vận Hành Thực Tế (Observability Headers)
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Đo đạc độ trễ phản hồi, PoP biên Cloudflare và tầng định tuyến trực tiếp
            </div>
          </div>

          <button
            className="btn btn--secondary btn--sm"
            onClick={handleTest}
            disabled={testing}
          >
            {testing ? 'Đang đo đạc...' : 'Gửi Probe Thử Nghiệm'}
          </button>
        </div>

        {testResult ? (
          <div style={{
            background: testResult.ok ? 'rgba(16, 185, 129, 0.05)' : 'rgba(239, 68, 68, 0.05)',
            border: `1px solid ${testResult.ok ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            borderRadius: '12px',
            padding: '18px',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '18px' }}>{testResult.ok ? '✅' : '❌'}</span>
              <span style={{ fontSize: '14px', fontWeight: 700, color: testResult.ok ? '#34d399' : '#f87171' }}>
                {testResult.ok ? `Kết Nối Thành Công (HTTP ${testResult.status})` : `Kết Nối Thất Bại: ${testResult.error}`}
              </span>
            </div>

            {testResult.ok && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '10px 14px', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>X-Erix-Routed-Tier</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#38bdf8', marginTop: '2px' }}>
                    {testResult.routed_tier || 'edge-cloudflare-workers-ai'}
                  </div>
                </div>

                <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '10px 14px', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>X-Erix-Latency-Ms</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#34d399', marginTop: '2px' }}>
                    {testResult.latency_ms} ms
                  </div>
                </div>

                <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '10px 14px', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>X-Erix-Edge-Colo</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#fbbf24', marginTop: '2px' }}>
                    PoP: {testResult.edge_colo || 'HAN / SGN'}
                  </div>
                </div>

                <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '10px 14px', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>X-Request-Id</div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {testResult.request_id}
                  </div>
                </div>
              </div>
            )}

            {testResult.reply && (
              <div style={{ background: 'rgba(0,0,0,0.3)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>Model Reply:</div>
                <div style={{ fontSize: '13px', color: 'var(--text-primary)', fontStyle: 'italic' }}>
                  "{testResult.reply}"
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '30px',
            color: 'var(--text-muted)',
            fontSize: '13px',
            background: 'rgba(255, 255, 255, 0.01)',
            borderRadius: '12px',
            border: '1px dashed var(--border)'
          }}>
            Bấm <strong>"Gửi Probe Thử Nghiệm"</strong> để gửi request ping kiểm tra định tuyến và đo độ trễ thực tế tới <code>ai-proxy.wowerix.com</code>.
          </div>
        )}
      </div>
    </div>
  );
}
